import React from 'react';
import { RealTimeStatusModel } from '../../services/api/paymentsApi';
import { 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Typography,
  Paper
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// KPI Card interface
interface KpiCardProps {
  title: string;
  value: number;
  status?: 'success' | 'warning' | 'danger' | 'info';
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'none';
  };
  onClick?: () => void;
}

// Single KPI Card
const KpiCard: React.FC<KpiCardProps> = ({ 
  title, 
  value, 
  status = 'info', 
  trend,
  onClick 
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success': return { bg: '#e6f7ed', text: '#2e7d32' };
      case 'warning': return { bg: '#fff8e1', text: '#ef6c00' };
      case 'danger': return { bg: '#ffebee', text: '#c62828' };
      case 'info': default: return { bg: '#e3f2fd', text: '#0d47a1' };
    }
  };

  const colors = getStatusColor();

  return (
    <Card 
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        borderLeft: 4,
        borderColor: colors.text,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'none',
          boxShadow: onClick ? 3 : 1
        }
      }}
    >
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: colors.text }}>
          {value}
        </Typography>
        {trend && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mt: 1,
              color: trend.direction === 'up' ? 'success.main' : 
                    trend.direction === 'down' ? 'error.main' : 
                    'text.secondary'
            }}
          >
            {trend.direction === 'up' && <ArrowUpwardIcon fontSize="small" />}
            {trend.direction === 'down' && <ArrowDownwardIcon fontSize="small" />}
            <Typography variant="body2" sx={{ ml: 0.5 }}>
              {trend.value}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Dashboard KPI Cards Grid
interface DashboardCardsProps {
  data: RealTimeStatusModel;
  onCardClick?: (key: keyof RealTimeStatusModel) => void;
}

const DashboardCards: React.FC<DashboardCardsProps> = ({ data, onCardClick }) => {
  // Map data to card configuration with status
  const cardConfig = [
    { 
      key: 'totalPaymentsProcessed', 
      title: 'Total Payments', 
      status: 'info',
      trend: { value: 4.5, direction: 'up' as const }
    },
    { 
      key: 'psd2CriticalInTransit', 
      title: 'Critical In Transit', 
      status: data.psd2CriticalInTransit > 10 ? 'danger' : data.psd2CriticalInTransit > 5 ? 'warning' : 'info',
      trend: { value: 2, direction: 'up' as const }
    },
    { 
      key: 'awaitingRepair', 
      title: 'Awaiting Repair', 
      status: data.awaitingRepair > 10 ? 'danger' : data.awaitingRepair > 5 ? 'warning' : 'info',
      trend: { value: 0, direction: 'none' as const }
    },
    { 
      key: 'suspectedDuplicate', 
      title: 'Suspected Duplicates', 
      status: data.suspectedDuplicate > 0 ? 'warning' : 'info',
      trend: { value: 1, direction: 'up' as const }
    }
  ];

  // Additional cards that can be added to a second row
  const additionalCards = [
    { 
      key: 'sanctionsScreeningFailed', 
      title: 'Sanctions Screening', 
      status: data.sanctionsScreeningFailed > 0 ? 'danger' : 'info',
    },
    { 
      key: 'fraudCheckHeld', 
      title: 'Fraud Timeout', 
      status: data.fraudCheckHeld > 0 ? 'danger' : 'info',
    },
    { 
      key: 'sanctionsCheckFailed', 
      title: 'Sanctions Failed', 
      status: data.sanctionsScreeningFailed > 0 ? 'danger' : 'info',
    },
    { 
      key: 'ctInitFailed', 
      title: 'CT Init Failed', 
      status: 'danger',
    },
    { 
      key: 'reversalFailed', 
      title: 'Reversed', 
      status: 'danger',
    },
    { 
      key: 'lookupFailed', 
      title: 'Lookup Failed', 
      status: 'danger',
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {cardConfig.map(({ key, title, status, trend }) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <KpiCard
              title={title}
              value={data[key as keyof RealTimeStatusModel] || 0}
              status={status as 'success' | 'warning' | 'danger' | 'info'}
              trend={trend}
              onClick={onCardClick ? () => onCardClick(key as keyof RealTimeStatusModel) : undefined}
            />
          </Grid>
        ))}
      </Grid>
      
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>Payment Status</Typography>
      <Grid container spacing={2}>
        {additionalCards.map(({ key, title, status }) => (
          <Grid item xs={6} sm={4} md={2} key={key}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                cursor: onCardClick ? 'pointer' : 'default',
                borderLeft: '3px solid',
                borderColor: status === 'danger' ? 'error.main' : status === 'warning' ? 'warning.main' : 'primary.main'
              }}
              onClick={onCardClick ? () => onCardClick(key as keyof RealTimeStatusModel) : undefined}
            >
              <Typography variant="body2" color="text.secondary">{title}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
                {data[key as keyof RealTimeStatusModel] || 0}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardCards; 